import React, { useEffect, useRef, useState } from "react";

/**
 * FanFacetCol
 * - count: number or string (e.g. 75000, "684+", "75,000")
 * - title: string
 * - duration: animation duration in ms
 */
const FanFacetCol = ({ count = 0, title = "", duration = 500 }) => {
  const [displayCount, setDisplayCount] = useState(0);
  const elementRef = useRef(null);
  const startedRef = useRef(false);
  const rafRef = useRef(null);

  // easing for smooth effect
  const easeOutQuad = (t) => 1 - (1 - t) * (1 - t);

  // helper: parse a numeric part and return { value: number, suffix: string }
  const parseCount = (cnt) => {
    // convert to string and trim
    const s = String(cnt).trim();

    // try to extract first number (supports commas and decimals)
    const numMatch = s.replace(/,/g, "").match(/-?\d+(\.\d+)?/);
    const value = numMatch ? Number(numMatch[0]) : 0;

    // suffix: everything after the matched number in the original string
    let suffix = "";
    if (numMatch) {
      const idx = s.indexOf(numMatch[0]) + numMatch[0].length;
      suffix = s.slice(idx);
    } else {
      // if no numeric match, keep the original non-numeric part as suffix
      suffix = s;
    }

    return { value: Number.isFinite(value) ? value : 0, suffix: suffix || "" };
  };

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const { value: numericCount } = parseCount(count);

    // make sure we have a number
    const end = Number.isFinite(numericCount) ? numericCount : 0;

    // animation function
    const animateCount = (start, endValue, ms) => {
      let startTime = null;

      const step = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / ms, 1);
        const easedProgress = easeOutQuad(progress);
        const current = Math.floor(start + (endValue - start) * easedProgress);
        setDisplayCount(current);

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          // ensure exact final value
          setDisplayCount(endValue);
        }
      };

      // cancel any running frame
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            animateCount(0, end, Math.max(50, duration)); // clamp duration to min 50ms
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, duration]); // rerun if count or duration change

  // parse suffix again for display (keeps suffix if count prop is string like "684+")
  const parsed = (() => {
    const s = String(count).trim();
    const numMatch = s.replace(/,/g, "").match(/-?\d+(\.\d+)?/);
    const suffix = numMatch ? s.slice(s.indexOf(numMatch[0]) + numMatch[0].length) : s;
    return suffix || "";
  })();

  return (
    <div className="fan-facet-single-item" ref={elementRef}>
      <h1>
        <span className="counter">
          {displayCount.toLocaleString()}
          {parsed}
        </span>
      </h1>
      <span className="counter-title">{title}</span>
    </div>
  );
};

export default FanFacetCol;
